import {
  canIntent,
  canIntentForOrg,
  canIntentForFollowingGroup,
  createPaymentIntent,
  createPaymentIntentForOrg,
  createPaymentIntentForFollowingGroup,
  canIntentForSticker,
  createStickerPurchaseIntent,
  validateIntentForSticker,
  validateIntentForStickerPublic,
  createInitiateWalletIntent,
  curatedBenevityMarketPlaceUpgrade,
  purchaseAnonymousPaidSticker,
} from '@/graphs/gql';
import { canIntentForWalletPricing, getCouponByPromoCode } from '@/graphs/payment';
import globalShared from '@/shared';
import withGraphErrors from '@/utils/graphqlResponse';

export default function shared(): { [key: string]: any } {
  const {
    ref,
    route,
    router,
    makeGraphRequest,
  } = globalShared();

  const loading = ref(false);

  async function requestCanIntent(userId: string): Promise<any> {
    // console.log(canIntent);
    const response = await makeGraphRequest(
      'query', canIntent, { userId }, null,
    );
    return response.data.canIntent;
  }

  async function requestCanIntentForOrg(userId: string, orgId: string): Promise<any> {
    const response = await makeGraphRequest(
      'query', canIntentForOrg, { userId, orgId }, null,
    );
    return response.data.canIntentForOrg;
  }

  async function requestCanIntentForFollowingGroup(userId: string, followingGroupId: string): Promise<any> {
    const response = await makeGraphRequest(
      'query', canIntentForFollowingGroup, { userId, followingGroupId }, null,
    );
    return response.data.canIntentForFollowingGroup;
  }

  async function requestCreatePaymentIntent(
    userId: string,
    paymentInput: { [key: string]: any },
  ): Promise<any> {
    // console.log('0-000E;');
    // console.log(userId);
    // console.log(paymentInput);
    const response = await makeGraphRequest(
      'mutation',
      createPaymentIntent,
      { userId, item: paymentInput },
      null,
    );
    return response.data.createPaymentIntent;
  }

  async function requestCreatePaymentIntentForOrg(
    userId: string,
    orgId: string,
    paymentInput: { [key: string]: any },
  ): Promise<any> {
    const response = await makeGraphRequest(
      'mutation',
      createPaymentIntentForOrg,
      { userId, orgId, item: paymentInput },
      null,
    );
    return response.data.createPaymentIntentForOrg;
  }

  async function requestCreatePaymentIntentForFollowingGroup(
    userId: string,
    followingGroupId: string,
    paymentInput: { [key: string]: any },
    orgId?: string,
  ): Promise<any> {
    const response = await makeGraphRequest(
      'mutation',
      createPaymentIntentForFollowingGroup,
      { userId, followingGroupId, item: paymentInput, orgId },
      null,
    );
    return response.data.createPaymentIntentForFollowingGroup;
  }

  async function requestCanIntentForSticker(stickerId: string): Promise<any> {
    const response = await makeGraphRequest(
      'query', canIntentForSticker, { stickerId }, null,
    );
    return response.data.canIntentForSticker;
  }

  async function requestValidateIntentForSticker(
    stickerId: string, entity: string, entityId: string,
  ): Promise<any> {
    const response = await makeGraphRequest(
      'query', validateIntentForSticker, { stickerId, entity, entityId }, null,
    );
    return response.data.validateIntentForSticker;
  }

  async function requestValidateIntentForStickerPublic(
    stickerId: string, entity: string, entityId: string,
  ): Promise<any> {
    const response = await makeGraphRequest(
      'query', validateIntentForStickerPublic, { stickerId, entity, entityId }, null,
    );
    return response.data.validateIntentForStickerPublic;
  }

  async function requestCreateStickerPurchaseIntent(
    input: { [key: string]: any },
  ): Promise<any> {
    const response = await makeGraphRequest(
      'mutation',
      createStickerPurchaseIntent,
      { input },
      null,
    );
    return response.data.createStickerPurchaseIntent;
  }

  /** Resolves to `{ data, errors }`; read it with `readWalletIntentResponse`. */
  async function requestCreateInitiateWalletIntent(
    input: { [key: string]: any },
  ): Promise<any> {
    return withGraphErrors(makeGraphRequest(
      'mutation',
      createInitiateWalletIntent,
      { input },
      null,
    ));
  }

  async function requestCanIntentForWalletPricing(walletPricingId: string): Promise<any> {
    const response = await makeGraphRequest(
      'query', canIntentForWalletPricing, { walletPricingId }, null,
    );
    return response.data.canIntentForWalletPricing;
  }

  async function requestCuratedMarketPlaceUpgrade(): Promise<any> {
    const response = await makeGraphRequest(
      'query', curatedBenevityMarketPlaceUpgrade, {}, null,
    );
    return response.data.curatedBenevityMarketPlaceUpgrade;
  }

  async function requestCouponByPromoCode(promotionCode: string): Promise<any> {
    const response = await makeGraphRequest(
      'query', getCouponByPromoCode, { promotionCode }, null,
    );
    if (response.data) {
      return response.data.getCouponByPromoCode;
    }
    return null;
  }

  async function requestPurchaseAnonymousPaidSticker(
    intentId: string,
    clientSecret: string,
    email: string,
    phone: string,
    firstName: string,
    lastName: string,
    embedOrganizationId?: string,
    embedHost?: string,
  ): Promise<any> {
    const response = await makeGraphRequest(
      'mutation',
      purchaseAnonymousPaidSticker,
      {
        intentId,
        clientSecret,
        email,
        phone,
        firstName,
        lastName,
        embedOrganizationId,
        embedHost,
      },
      null,
    );
    return response.data.purchaseAnonymousPaidSticker;
  }

  return {
    loading,
    route,
    router,
    requestCanIntent,
    requestCanIntentForOrg,
    requestCanIntentForFollowingGroup,
    requestCreatePaymentIntent,
    requestCreatePaymentIntentForOrg,
    requestCreatePaymentIntentForFollowingGroup,
    requestCanIntentForSticker,
    requestValidateIntentForSticker,
    requestValidateIntentForStickerPublic,
    requestCreateStickerPurchaseIntent,
    requestCreateInitiateWalletIntent,
    requestCanIntentForWalletPricing,
    requestCuratedMarketPlaceUpgrade,
    requestCouponByPromoCode,
    requestPurchaseAnonymousPaidSticker,
  };
}
